import { generateId } from '@vedalib/editor/lib/utils/id'

import { ScormVersionEnum } from 'gql/__generated__/graphql'
import { getWindowData } from 'services/ScormData/ScormData'

import {
  getAnalyticsConfig,
  getLmsUrlParams,
  getSessionId,
  buildAnonymousActor,
} from './config/lms-config'
import type { IAnalyticsConfig } from './config/lms-config'
import { getLaunchDomain, resolveSourceContext } from './middleware/lms.utils'
import { Cmi5Provider, PortalProvider, ScormProvider, XapiProvider } from './providers'
import type { ScormRuntimeVersion } from './transport/common'
import { ScormSuspendData, IInteractionData } from './transport/types'
import type {
  CompletionStatus,
  ILmsEvent,
  ILmsLearnerInfo,
  ILmsProvider,
  IProviderConfig,
  IScore,
  IXapiActor,
  LmsType,
  SuccessStatus,
} from './types'
import { findAPI } from './utils/find-api'

export interface ILrsConfigResponse {
  enabled: boolean
  endpoint?: string
  auth?: string
  error?: string
  sessionId?: string
}

const PUBLIC_XAPI_VERB_ID_PREFIX = 'http://adlnet.gov/expapi/'
const PUBLIC_CMI5_VERB_ID_PREFIX = 'https://w3id.org/xapi/cmi5/'

/**
 * LmsService - unified facade for all LMS interactions
 *
 * Supports master/slave pattern:
 * - Master: SCORM, xAPI, or cmi5 provider for state management
 * - Slaves: Additional providers for analytics
 */
export class LmsService {
  private static _instance: LmsService | null = null

  private masterProvider: ILmsProvider | null = null
  private slaveProviders: ILmsProvider[] = []
  private learnerInfo: ILmsLearnerInfo = {}
  private initialized = false
  private providerConfig: IProviderConfig | null = null

  private constructor() {}

  static get instance(): LmsService {
    if (!LmsService._instance) {
      LmsService._instance = new LmsService()
    }
    return LmsService._instance
  }

  static reset(): void {
    if (LmsService._instance) {
      void LmsService._instance.terminate()
      LmsService._instance = null
    }
  }

  isInitialized(): boolean {
    return this.initialized
  }

  getMasterType(): LmsType {
    return this.masterProvider?.type ?? 'none'
  }

  // --- Initialization ---

  async initialize(): Promise<boolean> {
    if (this.initialized) {
      return true
    }

    const urlParams = new URLSearchParams(window.location.search)
    const hasScormProxy = urlParams.get('scorm_proxy') === 'postmessage'
    const isDispatchContent = urlParams.get('dispatch') === 'true'

    // Build base config for providers
    this.providerConfig = this.buildProviderConfig()

    // 0. Portal V2 inline player — direct HTTP transport, no window.API needed.
    // Detected by window.__PORTAL_V2__ flag set by PortalV2Player before mounting.
    // Uses early-return so the else-if chain below stays valid brace-style.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).__PORTAL_V2__) {
      console.info('[LmsService] Initializing Portal V2 provider')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const portalSession = (window as any).__PORTAL_V2_SESSION__ || {}
      // Portal V2 ships its own sessionId; without it, buildProviderConfig
      // would fall back to getSessionId(SCORM-export-fetch-url) — null for V2
      // — and the slave XapiProvider would omit the sessionId extension on
      // every statement, breaking the LrsService post-filter (LrsService.ts:87).
      if (portalSession.sessionId) {
        this.providerConfig = { ...this.providerConfig, sessionId: portalSession.sessionId }
      }
      const portalProvider = new PortalProvider({
        sessionId: portalSession.sessionId || '',
        sessionToken: portalSession.sessionToken || '',
        initialCmiData: portalSession.cmiData || {},
        endpoint: portalSession.endpoint || '',
        email: portalSession.email,
        userName: portalSession.userName,
        onTerminated: portalSession.onTerminated,
        onCommit: portalSession.onCommit,
      })
      const connected = await portalProvider.initialize(this.providerConfig)
      if (connected) {
        this.masterProvider = portalProvider
        this.learnerInfo = portalProvider.getLearnerInfo()
      }
      await this.initAnalyticsSlaves()
      this.initialized = true
      console.info(
        `[LmsService] Initialized. Master: portal, Slaves: ${this.slaveProviders.length}`,
      )
      return true
    }

    // 1. SCORM via Proxy (course opened via driver with postmessage)
    if (hasScormProxy) {
      console.info('[LmsService] Initializing SCORM via Proxy')
      const scormProvider = new ScormProvider()
      const connected = await scormProvider.initialize(this.providerConfig)

      if (connected) {
        this.masterProvider = scormProvider
        this.learnerInfo = scormProvider.getLearnerInfo()
      }
    } else if (!isDispatchContent && findAPI(window)) {
      // 2. SCORM API found but no proxy - redirect to driver
      console.info('[LmsService] Found SCORM API but no proxy, redirecting to Driver...')
      await this.reopenWithDriver()
      return false
    } else if (this.isCmi5()) {
      // 3. cmi5
      console.info('[LmsService] Initializing cmi5')
      const lmsParams = getLmsUrlParams()
      const cmi5Provider = new Cmi5Provider({ fetchUrl: lmsParams?.fetch })

      if (lmsParams) {
        this.providerConfig = {
          ...this.providerConfig,
          ...lmsParams,
          prefixId: PUBLIC_CMI5_VERB_ID_PREFIX,
        }
      }

      const connected = await cmi5Provider.initialize(this.providerConfig)
      if (connected) {
        this.masterProvider = cmi5Provider
        this.learnerInfo = cmi5Provider.getLearnerInfo()
      }
    } else if (this.isXapi()) {
      // 4. xAPI (Standalone)
      console.info('[LmsService] Initializing xAPI')
      const lmsParams = getLmsUrlParams()
      const xapiProvider = new XapiProvider()

      if (lmsParams) {
        this.providerConfig = {
          ...this.providerConfig,
          ...lmsParams,
          prefixId: PUBLIC_XAPI_VERB_ID_PREFIX,
        }
      }

      const connected = await xapiProvider.initialize(this.providerConfig)
      if (connected) {
        this.masterProvider = xapiProvider
        this.learnerInfo = xapiProvider.getLearnerInfo()
      }
    } else {
      // 5. Local mode - no master provider
      console.info('[LmsService] No LMS detected. Running in local mode.')
    }

    // Fetch analytics slave config
    await this.initAnalyticsSlaves()

    this.initialized = true
    console.info(
      `[LmsService] Initialized. Master: ${this.getMasterType()}, Slaves: ${this.slaveProviders.length}`,
    )

    return true
  }

  sendAnalyticsViaBeacon(event: ILmsEvent[]): boolean {
    let success = false

    // Send via master provider
    if (this.masterProvider) {
      const masterSuccess = this.masterProvider.sendAnalyticsViaBeacon(event)
      console.info(
        `[LmsService] Sent analytics via master (${this.getMasterType()}):`,
        masterSuccess,
      )
      success = success || masterSuccess
    }

    // Send via slave providers
    for (const slave of this.slaveProviders) {
      const slaveSuccess = slave.sendAnalyticsViaBeacon(event)
      console.info(`[LmsService] Sent analytics via slave (${slave.type}):`, slaveSuccess)
      success = success || slaveSuccess
    }

    return success
  }

  private buildProviderConfig(): IProviderConfig {
    const windowData = getWindowData()
    const uuid = generateId()
    const exportId = windowData?.artifactId || ''
    const projectId = windowData?.data?.projectId || ''
    const sessionId = getSessionId(windowData?.analyticsConfig?.fetchUrl || null)
    const { embedId } = resolveSourceContext(windowData?.artifactId)

    return {
      actor: buildAnonymousActor(),
      activityId: `urn:uuid:${exportId || uuid}`,
      courseId: projectId,
      exportId,
      embedId,
      uuid,
      sessionId,
    }
  }

  private async initAnalyticsSlaves(): Promise<void> {
    const analyticsConfig = getAnalyticsConfig()
    if (!analyticsConfig) {
      return
    }

    const override = analyticsConfig.override

    // If endpoint and auth are overridden, use them directly without fetching
    if (override.endpoint && override.auth) {
      console.info('[LmsService] Using overridden LRS config from analyticsConfig')
      const slaveProvider = new XapiProvider({ useLocalStatements: true })
      const actor = this.buildActorFromLearnerInfo(override)

      const slaveConfig: IProviderConfig = {
        ...this.providerConfig!,
        endpoint: override.endpoint,
        auth: override.auth,
        actor,
        prefixId: PUBLIC_XAPI_VERB_ID_PREFIX,
      }

      console.info('[LmsService] Connecting analytics slave with overridden config, actor:', actor)

      const connected = await slaveProvider.initialize(slaveConfig)
      if (connected) {
        this.slaveProviders.push(slaveProvider)
        console.info('[LmsService] Analytics slave connected with override')
      }
      return
    }

    // Otherwise fetch LRS config from server
    const lrsConfig = await this.fetchLrsConfig(analyticsConfig)
    if (!lrsConfig?.enabled || !lrsConfig.endpoint || !lrsConfig.auth) {
      return
    }

    const slaveProvider = new XapiProvider({ useLocalStatements: true })
    const actor = this.buildActorFromLearnerInfo(override)

    const slaveConfig: IProviderConfig = {
      ...this.providerConfig!,
      endpoint: lrsConfig.endpoint,
      auth: lrsConfig.auth,
      actor,
      prefixId: PUBLIC_XAPI_VERB_ID_PREFIX,
      fetchLrsConfig: this.fetchLrsConfig.bind(this),
    }

    console.info('[LmsService] Connecting analytics slave with actor:', actor)

    const connected = await slaveProvider.initialize(slaveConfig)
    if (connected) {
      this.slaveProviders.push(slaveProvider)
      console.info('[LmsService] Analytics slave connected')
    }
  }

  private async fetchLrsConfig(
    analyticsConfig: IAnalyticsConfig,
  ): Promise<ILrsConfigResponse | null> {
    const windowData = getWindowData()
    const override = analyticsConfig.override
    const maxRetries = 3
    const baseDelay = 1000

    const body = JSON.stringify({
      companyId: override.companyId || windowData?.data?.projects?.default?.project?.companyId,
      projectId: override.projectId || windowData?.data?.projectId,
      artifactId: override.artifactId || windowData?.artifactId,
      lmsInfo: {
        learnerId: override.learnerId || this.learnerInfo.learnerId,
        learnerName: override.learnerName || this.learnerInfo.learnerName,
        learnerEmail: override.learnerEmail || this.learnerInfo.learnerEmail,
        lmsType: override.lmsType || this.learnerInfo.lmsType,
        lmsVersion: override.lmsVersion || this.learnerInfo.lmsVersion,
        sessionId: override.sessionId || this.learnerInfo.sessionId,
      },
    })

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(`${analyticsConfig.fetchUrl}/lrs-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        })

        if (response.ok) {
          return await response.json()
        }

        if (response.status >= 400 && response.status < 500) {
          console.warn('[LmsService] LRS config not available:', response.status)
          return null
        }

        console.warn(
          `[LmsService] LRS config fetch attempt ${attempt + 1} failed:`,
          response.status,
        )
      } catch (error) {
        console.warn(`[LmsService] LRS config fetch attempt ${attempt + 1} error:`, error)
      }

      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }

    console.error('[LmsService] Failed to fetch LRS config after', maxRetries, 'attempts')
    return null
  }

  private buildActorFromLearnerInfo(override?: IAnalyticsConfig['override']): IXapiActor {
    const embedDomain = getLaunchDomain()
    const email =
      override?.learnerEmail || this.learnerInfo.learnerEmail || this.learnerInfo.learnerId
    const name = override?.learnerName || this.learnerInfo.learnerName || 'Anonymous'
    const mbox = email
      ? `mailto:${email.includes('@') ? email : `${email}@${embedDomain}`}`
      : buildAnonymousActor().mbox

    return { mbox, name, objectType: 'Agent' }
  }

  private async reopenWithDriver(): Promise<boolean> {
    const currentUrl = new URL(window.location.href)
    const driverUrl = new URL('./driver/index.html', currentUrl.origin + currentUrl.pathname)

    currentUrl.searchParams.forEach((value, key) => {
      driverUrl.searchParams.set(key, value)
    })

    try {
      const response = await fetch(driverUrl.toString(), { method: 'GET' })
      if (!response.ok) {
        console.error('[LmsService] Driver file not found')
        return false
      }
    } catch (error) {
      console.error('[LmsService] Error checking driver:', error)
      return false
    }

    window.location.replace(driverUrl.toString())
    return true
  }

  private isCmi5(): boolean {
    return getWindowData()?.settings?.scormSettings?.lmsVersion === ScormVersionEnum.cmi5
  }

  private isXapi(): boolean {
    const sessionConfig = getLmsUrlParams()
    return (
      getWindowData()?.settings?.scormSettings?.lmsVersion === ScormVersionEnum.xapi ||
      (!!sessionConfig?.endpoint && !!sessionConfig?.auth)
    )
  }

  // --- Public API ---

  getLearnerInfo(): ILmsLearnerInfo {
    return { ...this.learnerInfo }
  }

  async setSuspendData(data: ScormSuspendData): Promise<void> {
    await this.masterProvider?.setSuspendData(data)
  }

  async getSuspendData(): Promise<ScormSuspendData | null> {
    return (await this.masterProvider?.getSuspendData()) || null
  }

  getSuspendDataSync(): ScormSuspendData | null {
    return this.masterProvider?.getSuspendDataSync() || null
  }

  async setProgress(value: number): Promise<void> {
    await this.masterProvider?.setProgress(value)
    await Promise.all(this.slaveProviders.map((p) => p.setProgress(value)))
  }

  async setScore(score: IScore): Promise<void> {
    await this.masterProvider?.setScore(score)
    await Promise.all(this.slaveProviders.map((p) => p.setScore(score)))
  }

  async setCompletionStatus(status: CompletionStatus): Promise<void> {
    await this.masterProvider?.setCompletionStatus(status)
    await Promise.all(this.slaveProviders.map((p) => p.setCompletionStatus(status)))
  }

  async setSuccessStatus(status: SuccessStatus): Promise<void> {
    console.info('[LmsService] setSuccessStatus:', status, 'slaves:', this.slaveProviders.length)
    await this.masterProvider?.setSuccessStatus(status)
    await Promise.all(this.slaveProviders.map((p) => p.setSuccessStatus(status)))
  }

  async setLocation(location: string): Promise<void> {
    await this.masterProvider?.setLocation(location)
  }

  async trackEvent(event: ILmsEvent): Promise<void> {
    await this.masterProvider?.trackEvent(event)
    await Promise.all(this.slaveProviders.map((p) => p.trackEvent(event)))
  }

  async commit(): Promise<void> {
    await this.masterProvider?.commit()
    await Promise.all(this.slaveProviders.map((p) => p.commit()))
  }

  async terminate(): Promise<void> {
    await this.masterProvider?.terminate()
    this.masterProvider = null

    await Promise.all(this.slaveProviders.map((p) => p.terminate()))
    this.slaveProviders = []

    this.initialized = false
  }

  setInteraction(index: number, data: IInteractionData): void {
    this.masterProvider?.setInteraction(index, data)
  }

  getScormVersion(): ScormRuntimeVersion {
    return this.masterProvider?.getScormVersion() ?? 'none'
  }
}

// Re-export types for backwards compatibility
export type { IScore, CompletionStatus, SuccessStatus, ILmsLearnerInfo } from './types'
